import { createSelector } from 'reselect';
import { PENDING_POT_ELIG_411_0727, PENDING_POT_ELIG_411_0728, PENDING_POT_ELIG_411_0729 } from '../constants/cases';
import { getFormsNeededForFollowUp } from '../utils/cases';

export const tokenSelector = (state) => state.entities.token;

// Returns the time at which the token expires (application) in milliseconds
export const tokenExpirationTimeSelector = (state) => state.entities.tokenExpirationTime;
// Returns the time at which the session expires (admin) in milliseconds
export const sessionExpirationTimeSelector = (state) => state.entities.sessionExpirationTime;

export const emailSelector = (state) => state.entities.email || '';

export const eligibilityInfoSelector = (state) => state.entities.eligibilityInfo || {};

export const searchValuesSelector = (state) => state.entities.searchValues;

export const postPreliminaryCasesSelector = (state) => state.entities.postPreliminaryCases || [];

export const hasCrimeOutsideTexasSelector = (state) => state.entities.hasCrimeOutsideTexas;
export const hasCrimeBefore1990Selector = (state) => state.entities.hasCrimeBefore1990;

export const lookupEndDateSelector = (state) => state.entities.lookupEndDate;

// Admin base selectors
export const orgInfoSelector = (state) => state.entities.orgInfo || {};
export const appConfigSelector = (state) => state.entities.appConfig || {};
export const usersSelector = (state) => state.entities.users || [];
export const applicationsSelector = (state) => state.entities.applications || [];

// Application base selectors
export const personalInfoSelector = (state) => state.entities.personalInfo;
export const demographicInfoSelector = (state) => state.entities.demographicInfo;
export const incomeAndAssetsSelector = (state) => state.entities.incomeAndAssets;
export const expensesSelector = (state) => state.entities.expenses;
export const otherCriminalHistorySelector = (state) => state.entities.otherCriminalHistory;

export const needSPNSelector = createSelector(
  [eligibilityInfoSelector],
  eligibilityInfo => eligibilityInfo.needSPN
);

// Returns all cases from BE response
export const allCasesSelector = createSelector(
  [eligibilityInfoSelector],
  eligibilityInfo => eligibilityInfo.cases || []
);

// Returns all cases with a "hide status" flag
export const allCasesHiddenStatusSelector = createSelector(
  [allCasesSelector],
  allCases => allCases.map(curCase => ({ ...curCase, hideStatus: true }))
);

export const getEligibleCases = (cases) => 
  Array.isArray(cases) ? cases.filter((curCase) => 
  curCase.final_eligibility === 'nondisclosure' || curCase.final_eligibility === 'expunction') : [];

// Returns only nondisclosure eligible cases
export const eligibleCasesSelector = createSelector(
  [allCasesSelector],
  allCases => getEligibleCases(allCases)
);

// Returns all cases that are not eligible for nondisclosure
export const ineligibleCasesSelector = createSelector(
  [allCasesSelector],
  allCases => allCases.filter((curCase) => curCase.final_eligibility !== 'nondisclosure' && curCase.final_eligibility !== 'expunction')
);

// Returns 'ineligible' marked cases
export const purelyIneligibleCasesSelector = createSelector(
  [allCasesSelector],
  allCases => allCases.filter((curCase) => curCase.final_eligibility === 'ineligible')
);

// Returns nondisclosure eligible cases
export const nondisclosureEligibleCasesSelector = createSelector(
  [allCasesSelector],
  allCases => allCases.filter((curCase) => curCase.final_eligibility === 'nondisclosure')
);

// Returns expunction eligible cases
export const expunctionEligibleCasesSelector = createSelector(
  [allCasesSelector],
  allCases => allCases.filter((curCase) => curCase.final_eligibility === 'expunction')
);

// Returns not-yet-eligible cases
export const notYetEligibleCasesSelector = createSelector(
  [allCasesSelector],
  allCases => allCases.filter((curCase) => curCase.final_eligibility === 'not_yet_eligible')
);

// Returns could-not-determine-eligibility cases
export const couldNotDetermineCasesSelector = createSelector(
  [allCasesSelector],
  allCases => allCases.filter((curCase) => curCase.final_eligibility === 'could_not_determine')
);

// SPN Grouped Cases (used for confirm cases page)
// Returns an array of case arrays (each sub-array containing cases with the same SPN)
export const spnGroupedCasesSelector = createSelector(
  [allCasesSelector, (state, showStatus) => showStatus],
  (allCases, showStatus) => {
    return allCases.reduce((spnGroupedCases, curCase) => {
      // For the confirm cases page, we don't want to show eligibility statuses by default (can override with "showStatus")
      const caseToAdd = { ...curCase, hideStatus: showStatus ? false : true };
      // Check if there are any existing arrays grouped by the current case's SPN
      const indexOfMatchingSpnArray = spnGroupedCases.findIndex(
        (caseArr) => caseArr.length > 0 && caseArr[0].def_spn === caseToAdd.def_spn
      );
      // If there is no existing index, add a new array with the given case.
      if (indexOfMatchingSpnArray < 0) {
        spnGroupedCases.push([caseToAdd]);
      } else {
        // Otherwise, add the case to the existing matching array
        spnGroupedCases[indexOfMatchingSpnArray].push(caseToAdd);
      }
      return spnGroupedCases;
    }, []);
  }
);

// Returns array of cases that need to show follow-up questions for NDO form determination.
// (hiding eligibility status on these cases, since we are still gathering eligibility info)
export const followUpQuestionCasesSelector = createSelector(
  [postPreliminaryCasesSelector], 
  (postPreliminaryCases) => postPreliminaryCases.map(curCase => ({ ...curCase, hideStatus: true })).filter(
    (currCase) => getFormsNeededForFollowUp(currCase).length > 0
  )
)

// Returns whether or not we should ask preliminary veteran questions
export const askVeteranQuestionsSelector = createSelector(
  [allCasesSelector], 
  (allCases) => {
    const askVetTreatmentCrtQuestions = allCases.some(
      (currCase) => currCase[PENDING_POT_ELIG_411_0727]
    )
    const askVetReemploymentPrgQuestions = allCases.some(
      (currCase) => currCase[PENDING_POT_ELIG_411_0729]
    )
    return [askVetTreatmentCrtQuestions || askVetReemploymentPrgQuestions, askVetTreatmentCrtQuestions, askVetReemploymentPrgQuestions]
  }
)

// Returns whether or not we should ask preliminary human trafficking victim questions
// (we should if the user is potentially eligible for 411.0728 in any case)
export const askTraffickingVictimQuestionsSelector = createSelector(
  [allCasesSelector], 
  (allCases) => allCases.some(
    (currCase) => currCase[PENDING_POT_ELIG_411_0728]
  )
)

// Returns all cases that have followup field questions answered
export const casesWithFollowupResponsesSelector = createSelector(
  [allCasesSelector],
  (allCases) => allCases.filter(
    (currCase) => {
      return currCase.completed_vet_reemployment_prg_for_case != null
              || currCase.completed_vet_treatment_court_for_case != null
              || currCase.bac_under_015 != null
              || currCase.had_commercial_license_or_permit != null
              || currCase.used_ignition_interlock_device != null
              || currCase.committed_solely_as_trafficking_victim != null
              || currCase.commited_after_submitting_petition != null;
    }
  )
);


